/// <amd-module name="VSSUI/Components/Table/Table" />
import { IObservableValue, ObservableLike } from "VSS/Core/Observable";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { FocusOrMouseWithin, IFocusOrMouseWithinStatus } from "VSSUI/FocusOrMouseWithin";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { FocusZone, FocusZoneContext, FocusZoneDirection, FocusZoneKeyStroke } from "VSSUI/FocusZone";
import { IIconProps, Icon } from "VSSUI/Icon";
import { Intersection } from "VSSUI/Intersection";
import { getDefaultLinkProps } from "VSSUI/Link";
import { IListItemDetails, IListMaterializedStats, IListProps, IOverlayRenderProps, ISimpleListCell, List, renderListCell, DropdownList } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import * as Resources from "VSSUI/Resources.Widgets";
import { Orientation, Position, Sizer } from "VSSUI/Sizer";
import { Tooltip } from "VSSUI/TooltipEx";
import { KeyCode, css, getSafeId } from "VSSUI/Util";
import { IBehavior } from "VSSUI/Utilities/Behavior";
import { EventDispatch, IEventDispatch } from "VSSUI/Utilities/Dispatch";
import { getTabIndex } from "VSSUI/Utilities/Focus";
import { ScreenSizeConditional } from "VSSUI/Utilities/ScreenSize";
import * as React from "react";
import {
    ColumnJustification,
    IMeasurementStyle,
    ISimpleTableCell,
    ITable,
    ITableColumn,
    ITableColumnBehaviorProps,
    ITableHeaderCellProps,
    ITableProps,
    ITableRowDetails,
    ITableRowProps,
    SortOrder,
    TableColumnLayout,
    TableColumnStyle,
    TableRowRenderer
} from "./Table.Props";
import { TableBreakpoint } from "./TableBreakpoint";

/** Id used for the ColumnFill */
export const ColumnFillId = "_fill";

/**
 * ColumnFill is used to fill the remaining space in the parent element with an
 * empty column. This column can be used anywhere in the column order. Columns
 * that appear after this will be pushed to the right.
 */
export const ColumnFill: ITableColumn<{}> = {
    columnLayout: TableColumnLayout.none,
    id: ColumnFillId,
    renderCell: (rowIndex: number, columnIndex: number) => {
        return (
            <td
                key="col-fill"
                aria-colindex={columnIndex + 1}
                aria-hidden={true}
                className={css("bolt-table-cell bolt-list-cell", "col-" + columnIndex)}
                data-column-index={columnIndex}
                role="presentation"
            />
        );
    },
    renderHeaderCell: (columnIndex: number, tableColumn: ITableColumn<{}>): JSX.Element => {
        return (
            <th
                key="col-fill"
                aria-hidden={true}
                className={css(tableColumn.headerClassName, "bolt-table-header-cell bolt-table-header-cell-empty", "col-header-" + columnIndex)}
                data-column-index={columnIndex}
                role="presentation"
            />
        );
    },
    width: -100
};

/**
 * ITableState is the internal state managed by the Table.
 */
export interface ITableState<T> {
    columnBehaviors: Array<IBehavior<Partial<ITableColumnBehaviorProps<T>>, {}>>;
    eventDispatch: IEventDispatch;
    renderInvisible: boolean;

    /**
     * Internal key intended to force a re-render when columns visibility changes
     */
    visibleColumnsKey: string;

    maxWidth?: string;
    minWidth?: string;
    tableBehaviors: Array<IBehavior<Partial<ITableProps<T>>, Partial<ITable<T>>>>;
    tableWidth: string;
    newListEnabled: boolean;
}

/**
 * The Table is a multi-column List component with an optional header.
 */
export class Table<T> extends React.Component<ITableProps<T>, ITableState<T>> implements ITable<T> {
    public static defaultProps: Partial<ITableProps<any>> = {
        role: "grid",
        selectRowOnClick: true,
        showHeader: true,
        showLines: true,
        singleClickActivation: true,
        spacerWidth: 8
    };

    public static getDerivedStateFromProps<T>(props: Readonly<ITableProps<T>>, state: Readonly<ITableState<T>>): Partial<ITableState<T>> {
        const tableBehaviors: Array<IBehavior<Partial<ITableProps<T>>, Partial<ITable<T>>>> = [];
        const columnBehaviors: Array<IBehavior<ITableColumnBehaviorProps<T>, {}>> = [];

        // Build the set of behaviors columns have attached to them
        for (let columnIndex = 0; columnIndex < props.columns.length; columnIndex++) {
            const tableColumn = props.columns[columnIndex];
            if (tableColumn.behaviors) {
                for (const behavior of tableColumn.behaviors) {
                    columnBehaviors.push(behavior);
                }
            }
        }

        // Build the set of behaviors the table has attached to it
        if (props.behaviors) {
            tableBehaviors.splice(tableBehaviors.length, 0, ...props.behaviors);
        }

        return {
            columnBehaviors,
            tableBehaviors
        };
    }

    public currentElement = React.createRef<HTMLDivElement>();

    // Reference to the underlying list interface.
    private list = React.createRef<List<T>>();
    private dropdownList = React.createRef<DropdownList<T>>();

    constructor(props: Readonly<ITableProps<T>>) {
        super(props);

        let newListEnabled;
        if ((window as any).isNewListEnabled) {
            newListEnabled = (window as any).isNewListEnabled;
        } else {
            newListEnabled = document.body.classList.contains('dropdown-list-component-enabled');
            (window as any).isNewListEnabled = newListEnabled;
        }

        this.state = {
            columnBehaviors: [],
            eventDispatch: props.eventDispatch || new EventDispatch(),
            renderInvisible: !!props.tableBreakpoints,
            visibleColumnsKey: "",
            tableBehaviors: [],
            tableWidth: "",
            newListEnabled: newListEnabled
        };

        // Initialize any column behaviors.
        for (let columnIndex = 0; columnIndex < props.columns.length; columnIndex++) {
            const tableColumn = props.columns[columnIndex];
            if (tableColumn.behaviors) {
                for (const behavior of tableColumn.behaviors) {
                    if (behavior && behavior.initialize) {
                        behavior.initialize({ tableProps: props, columnIndex: columnIndex }, {}, this.state.eventDispatch);
                    }
                }
            }
        }

        // Initialize the supplied behaviors.
        if (props.behaviors) {
            for (const behavior of props.behaviors) {
                if (behavior.initialize) {
                    behavior.initialize(props, this, this.state.eventDispatch);
                }
            }
        }
    }

    public render(): JSX.Element {
        const { eventDispatch, renderInvisible, visibleColumnsKey, newListEnabled } = this.state;
        const {
            selectableText,
            className,
            columns,
            containerClassName,
            enforceSingleSelect,
            excludeTabStop,
            focuszoneProps,
            showLines,
            id,
            itemProvider,
            maxHeight,
            onActivate,
            onFocus,
            onSelect,
            pageSize,
            role,
            rowHeight,
            rowHeights,
            scrollable,
            selection,
            singleClickActivation,
            selectRowOnClick,
            showScroll,
            tableBreakpoints,
            virtualize
        } = this.props;

        const columnWidths: (number | IObservableValue<number>)[] = [];
        const spacerWidth = (this.props.spacerWidth || 0) * 2;
        let tableMaxWidth = spacerWidth;
        let tableMinWidth = spacerWidth;
        let tableWidth = spacerWidth;
        let hasBoundedColumn = false;
        let hasUnboundedColumn = false;
        let fill = false;
        let columnCount = 0;
        let columnFillCount = 0;

        // Compute the table size based on the current column definition. Size the sizes
        // are observable, we need to recompute each render to ensure we have the
        // proper values.
        for (const column of columns) {
            const { maxWidth, minWidth, widthStyle = IMeasurementStyle.Pixel } = column;
            const width = ObservableLike.getValue(column.width);

            // Add the column width the set of available columnWidths.
            columnWidths.push(column.width);

            if (width < 0) {
                // This is a variable width columnn so we will fill the container.
                fill = true;

                // Update the min/max width of the table based on the supplied value.
                // We use a minumum width of 100px if one isnt specified.
                tableMinWidth += minWidth ? minWidth : 0;

                // If all variable width columns have a maxWidth, let the table fill the available
                // space, but set the table's max width to the sum of the column widths/maxWidths.
                if (maxWidth) {
                    hasBoundedColumn = true;
                    tableMaxWidth += maxWidth;
                } else {
                    // If there are any variable width columns without maxWidth, let the table
                    // fill the available space with no maxWidth.
                    hasUnboundedColumn = true;
                }

                columnCount++;
            } else if (width > 0) {
                if (widthStyle === IMeasurementStyle.Pixel) {
                    tableWidth += width;
                    tableMinWidth += width;
                    tableMaxWidth += width;
                } else {
                    // @NOTE: For now we are going to estimate a rem = 16px which is the default.
                    //  We could attempt to measure this if an exact measurement is really important.
                    tableWidth += width * 16;
                    tableMinWidth += width * 16;
                    tableMaxWidth += width * 16;
                }

                columnCount++;
            }

            if (column.id === "_fill") {
                columnFillCount++;
            }
        }

        const listProps: IListProps<T> = {
            selectableText,
            ariaColumnCount: columnCount - columnFillCount,
            ariaLabel: this.props.ariaLabel,
            ariaRowOffset: this.props.showHeader ? 1 : 0,
            className: css(className, "bolt-table", showLines && "bolt-table-show-lines"),
            columnCount: columnCount + 2,
            enforceSingleSelect,
            eventDispatch,
            excludeTabStop,
            focuszoneProps,
            id,
            itemProvider,
            maxWidth: hasBoundedColumn && !hasUnboundedColumn ? tableMaxWidth + "px" : undefined,
            maxHeight,
            minWidth: tableMinWidth !== tableWidth ? tableMinWidth + "px" : undefined,
            onActivate,
            onFocus,
            onSelect,
            pageSize,
            renderHeader: this.renderHeader,
            renderLoadingRow: this.renderLoadingRow,
            renderRow: this.renderRow,
            role,
            rowHeight,
            rowHeights,
            selection,
            selectRowOnClick,
            singleClickActivation,
            showScroll,
            virtualize,
            width: fill ? "100%" : tableWidth + "px"
        };

        const firstActionableHeaderIndex = getActionableIndex(columns);
        if (firstActionableHeaderIndex >= 0) {
            // If the header is tabbable, the rows do not need to be since they are
            // in the focus zone.
            listProps.defaultTabbableRow = -1;
        }

        let table = (
            <div
                className={css(
                    containerClassName,
                    "bolt-table-container flex-grow",
                    renderInvisible && "invisible",
                    scrollable && "v-scroll-auto",
                    tableBreakpoints && "h-scroll-hidden"
                )}
                ref={this.currentElement}
            >
                {tableBreakpoints ? (
                    <TableBreakpoint columnWidths={columnWidths} onBreakpoint={this.onBreakpoint} breakpoints={tableBreakpoints} />
                ) : undefined}
                {
                    newListEnabled && listProps.role === "listbox" && !listProps.className?.includes("bolt-list-box-tree")
                    ? <DropdownList {...listProps} key={visibleColumnsKey} ref={this.dropdownList} renderHeader={() => <></>} />
                    : <List {...listProps} key={visibleColumnsKey} ref={this.list} />
                }
            </div>
        );

        if (scrollable) {
            table = <Intersection>{table}</Intersection>;
        }

        return table;
    }

    public componentDidMount() {
        // Mount any of the attached tableBehaviors.
        for (const behavior of this.state.tableBehaviors) {
            if (behavior.componentDidMount) {
                behavior.componentDidMount(this.props);
            }
        }

        for (const behavior of this.state.columnBehaviors) {
            if (behavior.componentDidMount) {
                behavior.componentDidMount({ tableProps: this.props });
            }
        }
    }

    public componentDidUpdate() {
        // Update any of the attached tableBehaviors.
        for (const behavior of this.state.tableBehaviors) {
            if (behavior.componentDidUpdate) {
                behavior.componentDidUpdate(this.props);
            }
        }

        for (const behavior of this.state.columnBehaviors) {
            if (behavior.componentDidUpdate) {
                behavior.componentDidUpdate({ tableProps: this.props });
            }
        }
    }

    public componentWillUnmount() {
        // Unmount any of the attached tableBehaviors.
        for (const behavior of this.state.tableBehaviors) {
            if (behavior.componentWillUnmount) {
                behavior.componentWillUnmount();
            }
        }

        for (const behavior of this.state.columnBehaviors) {
            if (behavior.componentDidUpdate) {
                behavior.componentDidUpdate({ tableProps: this.props });
            }
        }
    }

    public addOverlay(
        id: string,
        rowIndex: number,
        render: (props: IOverlayRenderProps) => React.ReactNode,
        zIndex: number = 0,
        columnIndex?: number
    ): void {
        if (this.list.current) {
            return this.list.current.addOverlay(id, rowIndex, render, zIndex, columnIndex);
        }
    }

    public removeOverlay(id: string): void {
        if (this.list.current) {
            return this.list.current.removeOverlay(id);
        }
    }

    public focusRow(rowIndex: number, direction: number = 1) {
        if (this.list.current) {
            return this.list.current.focusRow(rowIndex, direction);
        } else {
            return Promise.resolve();
        }
    }

    public getFocusIndex(): number {
        if (this.list.current) {
            return this.list.current.getFocusIndex();
        }

        return -1;
    }

    public getStats(): IListMaterializedStats {
        if (this.list.current) {
            return this.list.current.getStats();
        }

        return {
            firstMaterialized: -1,
            firstRendered: -1,
            lastMaterialized: -1,
            lastRendered: -1
        };
    }

    public scrollIntoView(rowIndex: number, options?: ScrollIntoViewOptions): void {
        if (this.list.current) {
            return this.list.current.scrollIntoView(rowIndex, options);
        }
    }

    private onBreakpoint = () => {
        const visibleColumnsKey = this.props.tableBreakpoints
            ? getVisibleColumnsAndIndices(this.props.columns)
                  .map(({ originalIndex }) => originalIndex)
                  .join(",")
            : "";

        // If any column has toggled its visibility, we have to re-render.
        if (this.state.renderInvisible || this.state.visibleColumnsKey !== visibleColumnsKey) {
            this.setState({ renderInvisible: false, visibleColumnsKey });
        }
    };

    private onColumnsChanged = (): boolean => {
        this.forceUpdate();
        return false;
    };

    private renderHeader = (): JSX.Element => {
        const { columns, showHeader, renderHeader, spacerWidth } = this.props;
        const widths: number[] = [];
        let proportionalTotal = 0;

        // Determine the percentage for proportionally sized columns.
        for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
            const width = ObservableLike.getValue(columns[columnIndex].width);
            if (width < 0) {
                proportionalTotal += width;
            }
        }

        // Compute the width of all columns based on the fixed/proportional values
        for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
            const width = ObservableLike.getValue(columns[columnIndex].width);
            widths[columnIndex] = width >= 0 ? width : -((width / proportionalTotal) * 100);
        }

        let header: JSX.Element | null = null;

        // 0 is a valid ScreenSize, so we have strict inequality checking here
        if (showHeader !== false) {
            header = renderHeader ? renderHeader(columns) : <TableHeader tableColumns={columns} />;
        }
        if (typeof showHeader === "function") {
            header = <ScreenSizeConditional condition={screenSize => showHeader(screenSize)}>{header}</ScreenSizeConditional>;
        }

        return (
            <React.Fragment>
                <colgroup>
                    <col
                        key="col-group-left-spacer"
                        style={{ width: spacerWidth === 0 ? spacerWidth + "%" : spacerWidth + "px" }}
                    />
                    {getVisibleColumnsAndIndices(columns).map(({ column, originalIndex }) => {
                        const { widthStyle = IMeasurementStyle.Pixel } = column;

                        return (
                            <Observer
                                key={"col-group-" + originalIndex}
                                width={{
                                    filter: this.onColumnsChanged,
                                    observableValue: column.width
                                }}
                            >
                                {() => (
                                    <col
                                        style={{
                                            width:
                                                Math.abs(widths[originalIndex]) +
                                                (widths[originalIndex] < 0 ? "%" : widthStyle === IMeasurementStyle.Pixel ? "px" : "rem")
                                        }}
                                    />
                                )}
                            </Observer>
                        );
                    })}
                    <col
                        key="col-group-right-spacer"
                        style={{ width: spacerWidth === 0 ? spacerWidth + "%" : spacerWidth + "px" }}
                    />
                </colgroup>
                {header}
            </React.Fragment>
        );
    };

    private renderLoadingRow = (rowIndex: number, details: IListItemDetails<T>): JSX.Element => {
        const { columns, renderLoadingRow } = this.props;
        const rowDetails: ITableRowDetails<T> = {
            ariaBusy: true,
            ariaRowOffset: details.ariaRowOffset,
            data: details.data,
            eventDispatch: this.state.eventDispatch,
            excludeFocusZone: true,
            excludeTabStop: this.props.excludeTabStop ?? details.excludeTabStop,
            itemProvider: this.props.itemProvider,
            listProps: details.listProps,
            onFocusItem: details.onFocusItem,
            renderSpacer: this.props.renderSpacer,
            selection: this.props.selection,
            singleClickActivation: this.props.onActivate && this.props.singleClickActivation
        };

        // If a custom row loading animation is available use it.
        if (renderLoadingRow) {
            return renderLoadingRow(rowIndex, rowDetails);
        }

        return <TableLoadingRow columns={columns} details={rowDetails} rowIndex={rowIndex} />;
    };

    private renderRow = (rowIndex: number, item: T, details: IListItemDetails<T>): JSX.Element => {
        const rowDetails: ITableRowDetails<T> = {
            selectableText: details.selectableText,
            ariaRowOffset: details.ariaRowOffset,
            eventDispatch: this.state.eventDispatch,
            data: details.data,
            excludeTabStop: this.props.excludeTabStop ?? details.excludeTabStop,
            itemProvider: this.props.itemProvider,
            listProps: details.listProps,
            onFocusItem: details.onFocusItem,
            renderSpacer: this.props.renderSpacer,
            selection: this.props.selection,
            singleClickActivation: this.props.onActivate && this.props.singleClickActivation,
            role: this.props.role === "tree" ? "treeitem" : undefined
        };

        // First determine if the item supplied a custom row rendering function, if not
        // attempt to use the global row rendering function.
        const renderRow: TableRowRenderer<T> = (item as any).renderRow || this.props.renderRow;
        if (renderRow) {
            return renderRow(rowIndex, item, rowDetails);
        }

        // If no custom row renderer is available use the default row renderer.
        return (
            <TableRow details={rowDetails} index={rowIndex} linkProps={(item as any).linkProps}>
                {renderColumns(rowIndex, this.props.columns, item, rowDetails)}
            </TableRow>
        );
    };
}

export function renderColumns<T>(rowIndex: number, columns: Array<ITableColumn<T>>, item: T, details: ITableRowDetails<T>) {
    return getVisibleColumnsAndIndices(columns).map(({ column }, columnIndex: number) => {
        return column.renderCell(rowIndex, columnIndex, column, item, rowIndex + (details.ariaRowOffset ? details.ariaRowOffset : 1), details.role);
    });
}

function getVisibleColumnsAndIndices<T>(columns: Array<ITableColumn<T>>) {
    return columns.map((column, index) => ({ column, originalIndex: index })).filter(({ column }) => ObservableLike.getValue(column.width));
}

interface ITableHeaderProps<T> {
    tableColumns: Array<ITableColumn<T>>;
}

function getActionableIndex<T>(columns: ITableColumn<T>[]) {
    return columns.findIndex(
        column => ObservableLike.getValue(column.width) !== 0 && ((column.behaviors && column.behaviors.length > 0) || !!column.sortProps)
    );
}

class TableHeader<T> extends React.Component<ITableHeaderProps<T>> {
    public render(): JSX.Element {
        const firstActionableIndex = getActionableIndex(this.props.tableColumns);
        return (
            <FocusZoneContext.Consumer>
                {rowContext => {
                    return (
                        <FocusZone direction={FocusZoneDirection.Horizontal}>
                            <thead>
                                <FocusWithin>
                                    {(focusStatus: IFocusWithinStatus) => {
                                        return (
                                            <tr
                                                aria-rowindex={1}
                                                className={css("bolt-table-header-row", focusStatus.hasFocus && "focused")}
                                                data-row-index={-1}
                                                onBlur={focusStatus.onBlur}
                                                onFocus={focusStatus.onFocus}
                                                role="row"
                                            >
                                                <th aria-hidden="true" key="left-spacer" role="presentation" className="bolt-table-header-border" />
                                                {getVisibleColumnsAndIndices(this.props.tableColumns).map(
                                                    ({ column, originalIndex }, columnIndex) => {
                                                        if (column.renderHeaderCell) {
                                                            return column.renderHeaderCell(
                                                                columnIndex,
                                                                column,
                                                                rowContext.focuszoneId,
                                                                originalIndex === firstActionableIndex
                                                            );
                                                        } else if (column.iconProps || column.name) {
                                                            return (
                                                                <TableHeaderCell
                                                                    key={"col-header-" + columnIndex}
                                                                    ariaLabel={column.ariaLabel || column.name}
                                                                    column={column}
                                                                    columnIndex={columnIndex}
                                                                    focuszoneId={rowContext.focuszoneId}
                                                                    isFirstActionableHeader={originalIndex === firstActionableIndex}
                                                                >
                                                                    <Tooltip overflowOnly={true} text={column.name}>
                                                                        <div className="bolt-table-header-cell-text text-ellipsis body-s">
                                                                            {column.iconProps && Icon(column.iconProps)}
                                                                            <span className={css(column.headerTitleClassName, column.required && "bolt-table-header-cell-text--required")}>{column.name}</span>
                                                                        </div>
                                                                    </Tooltip>
                                                                </TableHeaderCell>
                                                            );
                                                        } else {
                                                            return (
                                                                <th
                                                                    aria-colindex={columnIndex + 1}
                                                                    aria-label={column.ariaLabel || Resources.EmptyColumnHeaderLabel}
                                                                    aria-readonly={column.readonly !== undefined ? column.readonly : "true"}
                                                                    className="bolt-table-header-border"
                                                                    key={"col-header-" + columnIndex}
                                                                />
                                                            );
                                                        }
                                                    }
                                                )}
                                                <th aria-hidden="true" key="right-spacer" role="presentation" className="bolt-table-header-border" />
                                            </tr>
                                        );
                                    }}
                                </FocusWithin>
                            </thead>
                        </FocusZone>
                    );
                }}
            </FocusZoneContext.Consumer>
        );
    }
}

export interface ITableHeaderCellState {
    measuredWidth: number;
    isFocused: boolean;
}

let boltTableHeaderCellCount = 0;

export class TableHeaderCell<T> extends React.Component<ITableHeaderCellProps<T>, ITableHeaderCellState> {
    private element = React.createRef<HTMLTableCellElement>();
    private headerCellId: number;

    public state = { measuredWidth: 0, isFocused: false };

    public constructor(props: ITableHeaderCellProps<T>) {
        super(props);
        this.headerCellId = boltTableHeaderCellCount++;
    }

    public render(): JSX.Element {
        const { ariaLabel, column, columnIndex, focuszoneId, isFirstActionableHeader } = this.props;
        let sizer: JSX.Element | undefined;

        if (column.onSize) {
            var showDivider = !!column.showSizerDivider
            sizer = (
                <Observer width={column.width}>
                    {(props: { width: number }) => {
                        // If we are sizable we will either use the supplied width (desired), or the
                        // measured width if we are a proportional column.
                        let width = props.width;
                        if (width < 0) {
                            width = this.state.measuredWidth;
                        }

                        return (
                            <Sizer
                                className="bolt-table-header-sizer"
                                divider={showDivider}
                                keyboardStepMultiplier={24}
                                maxSize={column.maxWidth}
                                minSize={column.minWidth}
                                onSize={this.onSize}
                                onSizeEnd={this.props.column.onSizeEnd}
                                orientation={Orientation.row}
                                position={Position.near}
                                size={width}
                                tabIndex={ this.state.isFocused ? 0 : -1 }
                                getFocusedElement={e => this.element}
                            />
                        );
                    }}
                </Observer>
            );
        }

        return (
            <FocusZoneContext.Consumer>
                {cellContext => {
                    const actionable = (column.behaviors && column.behaviors.length > 0) || !!column.sortProps;
                    const { sortProps = {} } = column;

                    const sortIcon =
                        column.sortProps && sortProps.sortOrder !== undefined
                            ? Icon({
                                  className: "bolt-table-header-sort-icon body-s",
                                  iconName: sortProps.sortOrder === SortOrder.ascending ? "SortUp" : "SortDown"
                              })
                            : null;

                    let justificationClassName;
                    if (column.justification === ColumnJustification.Left) {
                        justificationClassName = "justify-start";
                    } else if (column.justification === ColumnJustification.Right) {
                        justificationClassName = "justify-end";
                    }

                    const colIndex = columnIndex + 1;
                    const childId = getSafeId("th-col-content-" + this.headerCellId);
                    return (
                        <th
                            role="columnheader"
                            aria-colindex={colIndex}
                            aria-label={ariaLabel}
                            aria-labelledby={!ariaLabel ? childId : undefined}
                            aria-readonly="true"
                            aria-sort={
                                sortProps.sortOrder !== undefined
                                    ? sortProps.sortOrder === SortOrder.ascending
                                        ? "ascending"
                                        : "descending"
                                    : undefined
                            }
                            className={css(
                                column.headerClassName,
                                "bolt-table-header-cell",
                                "col-header-" + columnIndex,
                                actionable && "bolt-table-header-cell-actionable"
                            )}
                            data-column-index={columnIndex}
                            data-focuszone={actionable && css(isFirstActionableHeader && focuszoneId, cellContext.focuszoneId)}
                            ref={this.element}
                            onFocus={ () => this.setState({ isFocused: true }) }
                            onBlur={ () => this.setState({ isFocused: false }) }
                            tabIndex={actionable || sizer ? 0 : -1}
                        >
                            <div className={css("bolt-table-header-cell-content flex-row", justificationClassName)}>
                                {column.justification === ColumnJustification.Right && sortIcon}
                                <div id={childId} className="scroll-hidden">
                                    {this.props.children}
                                </div>
                                {column.justification !== ColumnJustification.Right && sortIcon}
                                <div aria-hidden={!this.state.isFocused}>
                                    {sizer}
                                </div>
                            </div>
                        </th>
                    );
                }}
            </FocusZoneContext.Consumer>
        );
    }

    public componentDidMount() {
        this.updateMeasuredWidth();
    }

    public componentDidUpdate() {
        this.updateMeasuredWidth();
    }

    private onSize = (event: MouseEvent | KeyboardEvent, updatedSize: number) => {
        const { column } = this.props;

        // Ensure we havent had our column definition updated and onSize removed.
        if (column.onSize) {
            column.onSize(event, this.props.columnIndex, updatedSize, column);
        }
    };

    private updateMeasuredWidth() {
        const { column } = this.props;
        if (column.onSize && this.element.current && ObservableLike.getValue(column.width) < 0) {
            const measuredWidth = this.element.current.getBoundingClientRect().width;
            if (measuredWidth !== this.state.measuredWidth) {
                this.setState({ measuredWidth });
            }
        }
    }
}

export function TableRow<T>(props: ITableRowProps<T> & { children?: React.ReactNode }): JSX.Element {
    const onFocus = function (event: React.FocusEvent<HTMLElement>) {
        props.details.onFocusItem(props.index, event);
        const rowNumber = props.details.ariaPosInSet ?? props.index + ariaRowOffset;
        if (props.details.ariaSetSize) {
            if (props.linkProps) {
                Utils_Accessibility.announce(format(Resources.ClickableRowAnnouncementWithSize, rowNumber, props.details.ariaSetSize), true);
            } else if (role == "option") {
                Utils_Accessibility.announce(Resources.ListItem, true);
            } else {
                Utils_Accessibility.announce(format(Resources.RowAnnouncementWithSize, rowNumber, props.details.ariaSetSize), true);
            }
        } else {
            if (props.linkProps) {
                Utils_Accessibility.announce(format(Resources.ClickableRowAnnouncement, rowNumber), true);
            } else {
                Utils_Accessibility.announce(format(Resources.RowAnnouncement, rowNumber), true);
            }
        }
    };

    const postprocessKeyStroke = function (event: React.KeyboardEvent<HTMLElement>) {
        const nodeName = (event.target as HTMLElement).nodeName;
        if (!event.defaultPrevented && nodeName !== "INPUT" && nodeName !== "TEXTAREA") {
            if (event.which === KeyCode.leftArrow && rowElement.current) {
                rowElement.current.focus();
                event.preventDefault();
            }
        }

        return FocusZoneKeyStroke.IgnoreNone;
    };

    const [rowElement] = React.useState(() => React.createRef<HTMLTableRowElement | HTMLAnchorElement>());
    const { details, index, linkProps } = props;
    const {
        selectableText,
        ariaLabel,
        ariaBusy,
        ariaDescribedBy,
        ariaPosInSet,
        ariaRowOffset,
        ariaSetSize,
        excludeFocusZone,
        id,
        renderSpacer,
        role,
        selection,
        singleClickActivation
    } = details;

    let ariaChecked: boolean | undefined;
    let ariaSelected: boolean | undefined;
    if (role === "menuitemcheckbox") {
        ariaChecked = selection && selection.selected(index);
    } else {
        ariaSelected = selection && selection.selected(index);
    }

    const rowElem = (
        <FocusOrMouseWithin onFocus={onFocus}>
            {(focusOrMouseWithinStatus: IFocusOrMouseWithinStatus) => {
                return (
                    <FocusZoneContext.Consumer>
                        {rowContext => {
                            const rowProps = {
                                "aria-busy": ariaBusy,
                                "aria-checked": ariaChecked,
                                "aria-describedby": ariaDescribedBy,
                                "aria-label": ariaLabel,
                                "aria-rowindex":
                                    role === "menuitemcheckbox" || role === "option" || role === "presentation" ? undefined : index + ariaRowOffset,
                                "aria-posinset": ariaPosInSet === null ? undefined : ariaPosInSet,
                                "aria-selected": role === "presentation" ? undefined : ariaSelected,
                                "aria-setsize": ariaSetSize === null ? undefined : ariaSetSize,
                                className: css(
                                    props.className,
                                    "bolt-table-row bolt-list-row",
                                    index === 0 && "first-row",
                                    focusOrMouseWithinStatus.hasFocus && "focused",
                                    selection && selection.selected(index) && "selected",
                                    singleClickActivation && "single-click-activation",
                                    linkProps && "v-align-middle",
                                    selectableText && "selectable-text",
                                    document.body.classList.contains('dropdown-list-component-enabled') &&
                                        props?.className?.includes("bolt-list-box-row") && "dropdown-list"
                                ),
                                "data-focuszone":
                                    excludeFocusZone || (selection && !selection.selectable(index)) ? undefined : rowContext.focuszoneId,
                                "data-row-index": props.index,
                                id: getSafeId(id),
                                role: role || "row",
                                tabIndex: getTabIndex(details),
                                onBlur: focusOrMouseWithinStatus.onBlur,
                                onFocus: focusOrMouseWithinStatus.onFocus,
                                onMouseEnter: focusOrMouseWithinStatus.onMouseEnter,
                                onMouseLeave: focusOrMouseWithinStatus.onMouseLeave,
                                ref: rowElement as any
                            };
                            
                            let rowChildren = [];
                            if (rowProps?.className?.includes("dropdown-list")) {
                                rowChildren = [props.children];
                            } else {
                                rowChildren = [
                                    <td
                                        aria-hidden="true"
                                        key="left-spacer"
                                        className="bolt-table-cell-compact bolt-table-cell bolt-list-cell bolt-table-spacer-cell"
                                        role="presentation"
                                    >
                                        {renderSpacer && renderSpacer(index, true)}
                                    </td>,
                                    props.children,
                                    <td
                                        aria-hidden="true"
                                        key="right-spacer"
                                        className="bolt-table-cell-compact bolt-table-cell bolt-list-cell bolt-table-spacer-cell"
                                        role="presentation"
                                    >
                                        {renderSpacer && renderSpacer(index, false)}
                                    </td>
                                ];
                            }

                            

                            return (
                                <FocusZone direction={FocusZoneDirection.Horizontal} postprocessKeyStroke={postprocessKeyStroke}>
                                    {linkProps ? (
                                        <a {...getDefaultLinkProps(linkProps)} {...rowProps}>
                                            {rowChildren}
                                        </a>
                                    ) : (
                                        rowProps?.className?.includes("dropdown-list") ? (
                                            <li {...rowProps}>{rowChildren}</li>
                                        ) : (
                                            <tr {...rowProps}>{rowChildren}</tr>
                                        )
                                    )}
                                </FocusZone>
                            );
                        }}
                    </FocusZoneContext.Consumer>
                );
            }}
        </FocusOrMouseWithin>
    );

    if (details.tooltipProps) {
        return <Tooltip {...details.tooltipProps}>{rowElem}</Tooltip>;
    }
    return rowElem;
}

export function TableLoadingRow<T>(props: { columns: Array<ITableColumn<T>>; details: ITableRowDetails<T>; rowIndex: number }) {
    return (
        // Return the default row loading animation.
        <TableRow className="bolt-list-row-loading" details={props.details} index={props.rowIndex}>
            {getVisibleColumnsAndIndices(props.columns).map(({ column }, columnIndex) => {
                return SimpleTableCell({ columnIndex, children: renderLoadingCell(column.columnLayout) });
            })}
        </TableRow>
    );
}

export function TableCell<T>(props: {
    ariaLabel?: string;
    ariaRowIndex?: number;
    children?: React.ReactNode;
    className?: string;
    colspan?: number;
    columnIndex: number;
    role?: string;
    tableColumn?: ITableColumn<T>;
}) {
    const { ariaLabel, ariaRowIndex, className, colspan, columnIndex, role, tableColumn } = props;

    let justificationClassName;
    if (tableColumn) {
        if (tableColumn.justification === ColumnJustification.Left) {
            justificationClassName = "justify-cell-start";
        } else if (tableColumn.justification === ColumnJustification.Right) {
            justificationClassName = "justify-cell-end";
        }
    }

    return (
        props.tableColumn?.className?.includes("dropdown-list") ? (
            <span
                aria-colindex={role === "presentation" || role === "treeitem" ? undefined : columnIndex + 1}
                aria-label={role === "presentation" || role === "treeitem" ? undefined : ariaLabel}
                aria-readonly={role === "presentation" || role === "treeitem" ? undefined : tableColumn && tableColumn.readonly}
                aria-rowindex={role === "presentation" || role === "treeitem" ? undefined : ariaRowIndex}
                className={css(className, tableColumn && tableColumn.className, "bolt-table-cell bolt-list-cell", justificationClassName)}
                data-column-index={columnIndex}
                key={"col-" + columnIndex}
                role={role === "treeitem" ? "presentation" : role || "gridcell"}
            >
                {props.children}
            </span>
        ) : (
            <td
                aria-colindex={role === "presentation" || role === "treeitem" ? undefined : columnIndex + 1}
                aria-label={role === "presentation" || role === "treeitem" ? undefined : ariaLabel}
                aria-readonly={role === "presentation" || role === "treeitem" ? undefined : tableColumn && tableColumn.readonly}
                aria-rowindex={role === "presentation" || role === "treeitem" ? undefined : ariaRowIndex}
                className={css(className, tableColumn && tableColumn.className, "bolt-table-cell bolt-list-cell", justificationClassName)}
                colSpan={colspan}
                data-column-index={columnIndex}
                key={"col-" + columnIndex}
                role={role === "treeitem" ? "presentation" : role || "gridcell"}
            >
                {props.children}
            </td>
        )
    );
}

export function SimpleTableCell<T>(props: {
    ariaLabel?: string;
    ariaRowIndex?: number;
    children?: React.ReactNode;
    className?: string;
    colspan?: number;
    columnIndex: number;
    contentClassName?: string;
    role?: string;
    tableColumn?: ITableColumn<T>;
}) {
    const children = <div className={css(props.contentClassName, "bolt-table-cell-content flex-row flex-center")}>{props.children}</div>;
    return TableCell({
        ariaLabel: props.ariaLabel,
        ariaRowIndex: props.ariaRowIndex,
        children: children,
        className: props.className,
        colspan: props.colspan,
        columnIndex: props.columnIndex,
        role: props.role,
        tableColumn: props.tableColumn
    });
}

export function TwoLineTableCell<T>(props: {
    ariaRowIndex?: number;
    className?: string;
    columnIndex: number;
    colspan?: number;
    iconProps?: IIconProps;
    line1: React.ReactNode;
    line2: React.ReactNode;
    tableColumn?: ITableColumn<T>;
    trailingIconProps?: IIconProps;
    trailingLine1IconProps?: IIconProps;
}) {
    const rowClasses = "bolt-table-two-line-cell-item flex-row scroll-hidden";
    const line1 = (
        <div className={rowClasses}>
            {props.line1}
            {props.trailingLine1IconProps &&
                    Icon({ ...props.trailingLine1IconProps, className: css(props.trailingLine1IconProps.className, "flex-noshrink") })}
        </div>
    );
    const line2 = <div className={rowClasses}>{props.line2}</div>;
    const lines = (
        <>
            {line1}
            {line2}
        </>
    );

    let children: JSX.Element;
    if (props.iconProps) {
        children = (
            <div className={css(props.className, "bolt-table-cell-content flex-row flex-center")}>
                {Icon({ ...props.iconProps, className: css(props.iconProps.className, "bolt-table-two-line-cell-icon flex-noshrink") })}
                <div className="flex-column scroll-hidden">{lines}</div>
                {props.trailingIconProps &&
                    Icon({ ...props.trailingIconProps, className: css(props.trailingIconProps.className, "bolt-table-two-line-cell-icon flex-noshrink") })}
            </div>
        );
    } else {
        children = (
            <div className={css(props.className, "bolt-table-cell-content flex-column")}>
                {lines}
                {props.trailingIconProps &&
                    Icon({ ...props.trailingIconProps, className: css(props.trailingIconProps.className, "bolt-table-two-line-cell-icon flex-noshrink") })}
            </div>
        );
    }

    return TableCell({
        ariaRowIndex: props.ariaRowIndex,
        children: children,
        colspan: props.colspan,
        columnIndex: props.columnIndex,
        className: "bolt-table-two-line-cell",
        tableColumn: props.tableColumn
    });
}

export function renderEmptyCell<T>(rowIndex: number, columnIndex: number): JSX.Element {
    return <TableCell columnIndex={columnIndex} key={columnIndex} role="presentation" />;
}

/**
 * A basic cell renderer that works well for most simple columns. Gets the value of the
 * the {column.id} property in the given table item and displays it as a string
 *
 * @param rowIndex Index of the row being rendered
 * @param columnIndex Index of the column being rendered
 * @param tableColumn Column definition
 * @param tableItem The data item being rendered for the current row
 */
export function renderSimpleCell<T extends ISimpleTableCell>(
    rowIndex: number,
    columnIndex: number,
    tableColumn: ITableColumn<T>,
    tableItem: T,
    ariaRowIndex?: number
): JSX.Element {
    return renderSimpleCellValue(columnIndex, tableColumn, tableItem[tableColumn.id], ariaRowIndex);
}

/**
 * Renders a simple table cell value
 *
 * @param columnIndex Index of the column being rendered
 * @param tableColumn Column definition
 * @param tableCell Simple value to render as text
 */
export function renderSimpleCellValue<T extends ISimpleTableCell>(
    columnIndex: number,
    tableColumn: ITableColumn<T>,
    tableCell: string | number | ISimpleListCell,
    ariaRowIndex?: number
): JSX.Element {
    const { columnStyle } = tableColumn;

    // Do not include padding if the table cell has an href
    const hasLink = !!(tableCell && typeof tableCell !== "string" && typeof tableCell !== "number" && tableCell.href);

    return (
        <SimpleTableCell
            ariaRowIndex={ariaRowIndex}
            className={css(
                columnStyle === TableColumnStyle.Primary && "bolt-table-cell-primary",
                columnStyle === TableColumnStyle.Secondary && "bolt-table-cell-secondary",
                columnStyle === TableColumnStyle.Tertiary && "bolt-table-cell-tertiary"
            )}
            columnIndex={columnIndex}
            contentClassName={hasLink ? "bolt-table-cell-content-with-link" : undefined}
            key={columnIndex}
            tableColumn={tableColumn}
        >
            {tableCell && renderListCell(tableCell)}
        </SimpleTableCell>
    );
}

function getVariableLength() {
    return Math.random() * 80 + 20 + "%";
}

export function renderLoadingCell(columnLayout?: TableColumnLayout): JSX.Element | null {
    if (columnLayout === TableColumnLayout.singleLine || columnLayout === undefined) {
        return (
            <div className="shimmer shimmer-line" style={{ width: getVariableLength() }}>
                &nbsp;
            </div>
        );
    } else if (columnLayout === TableColumnLayout.singleLinePrefix) {
        return (
            <React.Fragment>
                <div className="shimmer shimmer-circle-small flex-noshrink" />
                <div className="shimmer shimmer-line" style={{ width: getVariableLength() }}>
                    &nbsp;
                </div>
            </React.Fragment>
        );
    } else if (columnLayout === TableColumnLayout.twoLine) {
        return (
            <div className="flex-column flex-grow">
                <div className="bolt-table-two-line-cell-item shimmer shimmer-line" style={{ width: getVariableLength() }}>
                    &nbsp;
                </div>
                <div className="bolt-table-two-line-cell-item shimmer shimmer-line" style={{ width: getVariableLength() }}>
                    &nbsp;
                </div>
            </div>
        );
    } else if (columnLayout === TableColumnLayout.twoLinePrefix) {
        return (
            <React.Fragment>
                <div className="shimmer shimmer-circle-large flex-noshrink" />
                <div className="flex-column flex-grow">
                    <div className="bolt-table-two-line-cell-item shimmer shimmer-line" style={{ width: getVariableLength() }}>
                        &nbsp;
                    </div>
                    <div className="bolt-table-two-line-cell-item shimmer shimmer-line" style={{ width: getVariableLength() }}>
                        &nbsp;
                    </div>
                </div>
            </React.Fragment>
        );
    }
    return null;
}
